import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useContext } from "react";
import EastIcon from "@mui/icons-material/East";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import {
  DRAWER_COLLAPSED_WIDTH,
  DRAWER_WIDTH,
  getNavSx,
  getSubMenuItemButtonSx,
  navItemWrapSx,
  navListSx,
  navScrollSx,
  sideNavLogoFooterSx,
  sideNavLogoImgSx,
  sideNavReturnBarSx,
  sideNavReturnButtonSx,
  sideNavSelectedIndicatorStyles,
  subMenuListSx,
} from "./SideNav.style";
import {
  findSideNavSelection,
  getNavItemNavigatePath,
  getVisibleSideNavItems,
} from "./SideNav.utils";
import azuriaLogo from "assets/Azuria-ws.png";

export const useSideNavItems = () => {
  const { userProfile } = useContext(UserContext);
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const profile = userProfile?.profile ?? {};
  const accessTo = (profile.accessTo ?? []).map((key) => String(key));

  return useMemo(
    () =>
      getVisibleSideNavItems({
        profile,
        usesEffectiveAccess,
        canAccessPage,
        accessTo,
      }),
    [profile, usesEffectiveAccess, canAccessPage, accessTo]
  );
};

const SideNav = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getCustomerName } = useContext(UserContext);
  const { canReturnToApplication } = useEffectiveAccess();
  const navItems = useSideNavItems();
  const [customerChangedSelected, setCustomerChangedSelected] = useState(false);

  const headerButton =
    INFRAMARK_CONSTANTS?.userRoutes?.userAdminRoutes?.headerButton;
  const customerName = getCustomerName?.() ?? "";
  const isCustomerSelected = customerName !== "" || customerChangedSelected;
  const showReturnButton = isCustomerSelected && canReturnToApplication() && !isCollapsed;

  useEffect(() => {
    const handleCustomerChange = (event) => {
      if (event?.detail?.groupName) {
        setCustomerChangedSelected(true);
      }
    };
    window.addEventListener("customerChanged", handleCustomerChange);
    return () =>
      window.removeEventListener("customerChanged", handleCustomerChange);
  }, []);

  const selection = useMemo(
    () => findSideNavSelection(navItems, pathname),
    [navItems, pathname]
  );

  const [selectedId, setSelectedId] = useState(selection.selectedId);
  const [selectedSubId, setSelectedSubId] = useState(selection.selectedSubId);
  const [subCollapsed, setSubCollapsed] = useState(false);

  useEffect(() => {
    setSelectedId(selection.selectedId);
    setSelectedSubId(selection.selectedSubId);
    setSubCollapsed(false);
  }, [selection.selectedId, selection.selectedSubId]);

  const currentDrawerWidth = isCollapsed
    ? DRAWER_COLLAPSED_WIDTH
    : DRAWER_WIDTH;

  const navigateToItem = (item, fallbackPath) => {
    const target = fallbackPath ?? getNavItemNavigatePath(item);
    if (target) {
      navigate(target);
    }
  };

  const handleNavItemClick = (item) => {
    if (item.id === selectedId && (item.subPages ?? []).length > 0) {
      setSubCollapsed((prev) => !prev);
      return;
    }

    setSelectedId(item.id);
    const firstSubId = (item.subPages ?? [])[0]?.id ?? null;
    setSelectedSubId(firstSubId);
    setSubCollapsed(false);
    navigateToItem(item);
  };

  const handleSubPageClick = (item, subPage) => {
    setSelectedId(item.id);
    setSelectedSubId(subPage.id);
    setSubCollapsed(false);
    navigateToItem(subPage, subPage.path);
  };

  if (navItems.length === 0) {
    return null;
  }

  return (
    <Box
      component="nav"
      sx={getNavSx({ currentDrawerWidth, isCollapsed })}
      aria-label="Primary"
    >
      {showReturnButton && headerButton?.label && (
        <Box sx={sideNavReturnBarSx}>
          <Button
            variant="text"
            endIcon={<EastIcon />}
            onClick={() => headerButton?.onClickHandler?.()}
            sx={sideNavReturnButtonSx}
          >
            {headerButton.label}
          </Button>
        </Box>
      )}
      <Box sx={navScrollSx}>
        <List sx={navListSx}>
          {navItems.map((item) => {
            const { id, label, Icon, subPages = [] } = item;
            const isSelected = selectedId === id;
            const hasSubPages = subPages.length > 0;
            const showSubMenu =
              isSelected && hasSubPages && !isCollapsed && !subCollapsed;

            const itemButton = (
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavItemClick(item)}
                title={label}
              >
                {isSelected && (
                  <Box
                    component="span"
                    sx={sideNavSelectedIndicatorStyles}
                    aria-hidden="true"
                  />
                )}
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                {!isCollapsed && <ListItemText primary={label} />}
              </ListItemButton>
            );

            return (
              <Box key={id} component="li" sx={navItemWrapSx}>
                <ListItem disablePadding>
                  {isCollapsed ? (
                    <Tooltip title={label} placement="right">
                      {itemButton}
                    </Tooltip>
                  ) : (
                    itemButton
                  )}
                </ListItem>
                {showSubMenu && (
                  <List component="div" disablePadding sx={subMenuListSx}>
                    {subPages.map((subPage) => {
                      const isSubSelected = selectedSubId === subPage.id;
                      return (
                        <ListItem key={subPage.id ?? subPage.label} disablePadding>
                          <ListItemButton
                            className="side-nav-sub-item"
                            selected={isSubSelected}
                            onClick={() => handleSubPageClick(item, subPage)}
                            sx={getSubMenuItemButtonSx(isSubSelected)}
                          >
                            <ListItemText primary={subPage.label} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            );
          })}
        </List>
      </Box>
      {!isCollapsed && (
        <Box sx={sideNavLogoFooterSx}>
          <Box
            component="img"
            src={azuriaLogo}
            alt="Azuria Water Solutions"
            sx={sideNavLogoImgSx}
          />
        </Box>
      )}
    </Box>
  );
};

SideNav.propTypes = {
  isCollapsed: PropTypes.bool,
};

export default SideNav;
